import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { EventTaskTableEditor } from './EventTaskTableEditor';
import { useCreateUserBotInline, useParseUserTasks } from '@/lib/sdk/adhoc/queries';
import { UserBotApi } from '@/lib/sdk/adhoc/api';
import { useToast } from '@/client/hooks/useToast';
import type { UserBotTaskInput, BulkTaskError } from '@/lib/sdk/adhoc/types';
import type { TeamMember } from '@/lib/sdk/team/types';

interface FormValues {
  name: string;
}

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

interface EventCreateDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  orgId: string;
  teamMembers: TeamMember[];
  onSaved?: () => void;
}

export function EventCreateDrawer({ isOpen, onClose, orgId, teamMembers, onSaved }: EventCreateDrawerProps) {
  const toast = useToast();
  const createUserBotInline = useCreateUserBotInline();
  const parseUserTasks = useParseUserTasks();
  const pendingNameRef = useRef<string>('');

  const [rowErrors, setRowErrors] = useState<BulkTaskError[]>([]);
  const [importErrors, setImportErrors] = useState<BulkTaskError[]>([]);
  const [editorErrors, setEditorErrors] = useState(0);
  const [importedRows, setImportedRows] = useState<UserBotTaskInput[] | undefined>(undefined);

  const { register, handleSubmit, formState: { errors }, setError, reset } = useForm<FormValues>();

  useEffect(() => {
    if (isOpen) {
      setRowErrors([]);
      setImportErrors([]);
      setEditorErrors(0);
      setImportedRows(undefined);
      pendingNameRef.current = '';
      reset();
    }
  }, [isOpen, reset]);

  const handleDownloadTemplate = async () => {
    try {
      const blob = await UserBotApi.getUserBotTemplate(orgId);
      triggerDownload(blob, 'event_tasks_template.xlsx');
    } catch {
      toast.error('Failed to download template');
    }
  };

  const handleImport = (file: File) => {
    setImportErrors([]);
    parseUserTasks.mutate({ orgId, file }, {
      onSuccess: (result) => {
        if (result.errors && result.errors.length > 0) {
          setImportErrors(result.errors);
          return;
        }
        setImportedRows(result.tasks ?? []);
      },
      onError: () => {
        toast.error('Failed to parse Excel file');
      },
    });
  };

  const handleValidRows = useCallback((rows: UserBotTaskInput[]) => {
    setEditorErrors(0);
    if (rows.length === 0) {
      toast.error('Add at least one task before creating the event');
      return;
    }
    const name = pendingNameRef.current;
    if (!name) return;
    pendingNameRef.current = '';
    createUserBotInline.mutate(
      { orgId, data: { name, tasks: rows } },
      {
        onSuccess: (result) => {
          if (result.errors && result.errors.length > 0) {
            setRowErrors(result.errors);
            return;
          }
          if (result.bot) {
            toast.success('Event created successfully');
            onSaved?.();
            onClose();
          }
        },
      }
    );
  }, [orgId, createUserBotInline, onSaved, onClose, toast]);

  const handleValidationError = useCallback((count: number) => {
    setEditorErrors(count);
    pendingNameRef.current = '';
  }, []);

  const onSubmit = (values: FormValues) => {
    setRowErrors([]);
    if (!values.name?.trim()) {
      setError('name', { type: 'required', message: 'Tasks group name is required' });
      return;
    }
    pendingNameRef.current = values.name.trim();
    document.getElementById('event-create-drawer-validate')?.click();
  };

  return (
    <Drawer isOpen={isOpen} onClose={onClose} placement="bottom" size="full">
      <DrawerOverlay />
      <DrawerContent maxH="80vh" bg="bg-surface" data-testid="ev-create-drawer">
        <DrawerCloseButton />
        <DrawerHeader borderBottomWidth="1px" borderColor="border-default" py={3}>
          <Text fontWeight="semibold">Add Tasks</Text>
        </DrawerHeader>

        <DrawerBody p={4} overflow="auto">
          <Box maxW="1200px" mx="auto">
            <form id="event-create-drawer-form" onSubmit={handleSubmit(onSubmit)}>
              <VStack spacing={4} align="stretch">
                <FormControl isInvalid={!!errors.name} isRequired>
                  <FormLabel color="text-primary" mb={1}>Tasks Group Name</FormLabel>
                  <Input
                    {...register('name')}
                    data-testid="ev-create-name-input"
                    placeholder="Enter tasks group name"
                    bg="bg-surface"
                    borderColor="border-default"
                    _hover={{ borderColor: 'border-hover' }}
                    _focus={{ borderColor: 'brand.500', boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)' }}
                  />
                  <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
                </FormControl>

                <EventTaskTableEditor
                  teamMembers={teamMembers}
                  onValidRows={handleValidRows}
                  onValidationError={handleValidationError}
                  validateTriggerId="event-create-drawer-validate"
                  importedRows={importedRows}
                  onDownloadTemplate={handleDownloadTemplate}
                  onImport={handleImport}
                  isImporting={parseUserTasks.isPending}
                />

                {editorErrors > 0 && (
                  <Text fontSize="sm" color="red.500">
                    {editorErrors} row{editorErrors !== 1 ? 's' : ''} have errors — fix before submitting.
                  </Text>
                )}

                {importErrors.length > 0 && (
                  <Box bg="red.50" borderWidth="1px" borderColor="red.200" borderRadius="md" p={3}>
                    <Text fontWeight="medium" color="red.700" mb={2} fontSize="sm">
                      Import errors ({importErrors.length}):
                    </Text>
                    <VStack align="stretch" spacing={1} maxH="120px" overflowY="auto">
                      {importErrors.map((err) => (
                        <Text key={err.row_number} fontSize="sm" color="red.600">
                          Row {err.row_number}: {err.message}
                        </Text>
                      ))}
                    </VStack>
                  </Box>
                )}

                {rowErrors.length > 0 && (
                  <Box bg="red.50" borderWidth="1px" borderColor="red.200" borderRadius="md" p={3}>
                    <Text fontWeight="medium" color="red.700" mb={2} fontSize="sm">
                      Errors ({rowErrors.length}):
                    </Text>
                    <VStack align="stretch" spacing={1} maxH="120px" overflowY="auto">
                      {rowErrors.map((err) => (
                        <Text key={err.row_number} fontSize="sm" color="red.600">
                          Row {err.row_number}: {err.message}
                        </Text>
                      ))}
                    </VStack>
                  </Box>
                )}
              </VStack>
            </form>
          </Box>
        </DrawerBody>

        <DrawerFooter borderTopWidth="1px" borderColor="border-default" py={3}>
          <HStack spacing={3} justify="flex-end" w="full">
            <Button type="button" variant="ghost" onClick={onClose} isDisabled={createUserBotInline.isPending} data-testid="ev-create-cancel-btn">
              Cancel
            </Button>
            <Button
              type="submit"
              form="event-create-drawer-form"
              colorScheme="brand"
              isLoading={createUserBotInline.isPending}
              loadingText="Creating..."
              data-testid="ev-create-submit-btn"
            >
              Add Tasks
            </Button>
          </HStack>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
